import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Truck, Loader2 } from 'lucide-react';

const Login = () => {
  const { user, login, loading } = useAuth();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (user) {
    const from = location.state?.from || '/dashboard';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const r = await login(username, password);
    if (!r.ok) setError(r.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left – brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 brand-gradient text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1603814929877-d5d927322656?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')", backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(217,91%,18%)]/85 via-[hsl(217,91%,22%)]/70 to-[hsl(217,91%,12%)]/90" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-amber-400 grid place-items-center text-[hsl(217,91%,18%)] shadow-lg">
            <Truck className="h-6 w-6" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-xl font-bold tracking-tight">ESD</div>
            <div className="text-xs text-blue-200/90">Equipment Solutions Department</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            Fleet operations,<br />simplified.
          </h1>
          <p className="mt-4 text-blue-100/90 max-w-md">
            End-to-end management of equipment requests, inspections, mobilizations, and billing
            for PIVI Equipment Solutions Department.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {['Equipment', 'Operators', 'Workflow'].map((t) => (
              <div key={t} className="rounded-md bg-white/10 backdrop-blur px-3 py-2 text-center text-sm border border-white/15">
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-blue-200/70">
          © {new Date().getFullYear()} PIVI Equipment Solutions Department. All rights reserved.
        </div>
      </div>

      {/* Right – login form */}
      <div className="flex items-center justify-center p-6 sm:p-12 subtle-grid bg-[hsl(210,40%,98%)]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-lg brand-gradient grid place-items-center">
              <Truck className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <div className="font-bold text-lg">ESD</div>
              <div className="text-xs text-muted-foreground">PIVI Equipment Solutions</div>
            </div>
          </div>
          <Card className="shadow-xl border-slate-200">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold">Sign in</CardTitle>
              <CardDescription>Enter your credentials to access the system</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. admin" autoFocus required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                </div>
                {error && (
                  <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
                )}
                <Button type="submit" className="w-full bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</> : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
